import React, { useContext, useMemo, useState } from 'react';
import { Alert, Box, Button, Divider, Paper, Stack, TextField, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FirebaseContext } from 'common';
import PublicLayout from '../components/PublicLayout.jsx';
import { resolveShopContext } from '../common/shopContext';
import { getSiteTheme, normalizeTemplateKey } from '../common/websiteTemplates';
import { clearCart, loadCart } from '../common/shopUtils';
import { formatMoney, getEffectiveProductPricing } from '../common/shopPricing';
import { isPartnerComingSoon, isPartnerMaintenance } from '../common/partnerUtils';
import PartnerUpsellPage from './PartnerUpsellPage';
import PartnerMaintenancePage from './PartnerMaintenancePage';
import PartnerComingSoonPage from './PartnerComingSoonPage';
import { getLocalizedShopItem } from '../common/shopContent';

export default function ShopCheckout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { partnerSlug } = useParams();
  const firebase = useContext(FirebaseContext);
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const { t, i18n } = useTranslation();

  const shopContext = useMemo(
    () =>
      resolveShopContext(settings, {
        partnerSlug,
        locale: i18n.language,
        pathname: location.pathname,
      }),
    [settings, partnerSlug, i18n.language, location.pathname]
  );

  const { partner, partnerKey, cartScopeKey, paths, websiteContent } = shopContext;
  const isAdmin = auth?.profile?.usertype === 'admin' || auth?.profile?.usertype === 'fleetadmin';
  const gateInactive = Boolean(partner?.inactive);
  const gateComingSoon = Boolean(partner && isPartnerComingSoon(partner) && !isAdmin);
  const gateMaintenance = Boolean(partner && isPartnerMaintenance(partner) && !isAdmin);

  const templateKey = normalizeTemplateKey(websiteContent.template || 'classic');
  const theme = getSiteTheme(templateKey, websiteContent.appearance || {});
  const shop = websiteContent.shop || {};
  const shopEnabled = shop.enabled !== false;
  const currency = shop.currency || 'USD';
  const campaigns = useMemo(() => (Array.isArray(shop.campaigns) ? shop.campaigns : []), [shop.campaigns]);
  const products = useMemo(() => {
    const list = Array.isArray(shop.products) ? shop.products : [];
    return list.map((p) => getLocalizedShopItem(p, i18n.language));
  }, [shop.products, i18n.language]);

  const [cart] = useState(() => loadCart(cartScopeKey));
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successOrderId, setSuccessOrderId] = useState('');

  const lines = useMemo(() => {
    return Object.entries(cart || {})
      .map(([productId, qty]) => {
        const product = products.find((p) => p && p.id === productId);
        if (!product || product.published === false) return null;
        const safeQty = Math.max(1, Number(qty || 0));
        const pricing = getEffectiveProductPricing(product, campaigns);
        const price = Number(pricing.finalPrice || 0);
        const savings = Number(pricing.savings || 0) * safeQty;
        return {
          productId,
          qty: safeQty,
          title: product.title,
          price,
          subtotal: price * safeQty,
          savings,
          campaign: pricing.campaign,
        };
      })
      .filter(Boolean);
  }, [cart, products, campaigns]);

  const total = lines.reduce((sum, l) => sum + Number(l.subtotal || 0), 0);
  const totalSavings = lines.reduce((sum, l) => sum + Number(l.savings || 0), 0);
  const isLoggedIn = Boolean(auth?.profile?.uid);

  if (gateInactive) {
    return <PartnerUpsellPage partner={partner} />;
  }

  if (gateComingSoon) {
    return <PartnerComingSoonPage partner={partner} />;
  }

  if (gateMaintenance) {
    return <PartnerMaintenancePage partner={partner} />;
  }

  const placeOrder = async () => {
    setSubmitError('');
    if (!isLoggedIn) {
      navigate('/login', { state: { from: paths.checkout } });
      return;
    }
    if (!lines.length) return;

    if (!firebase?.database?.ref) {
      setSubmitError(t('checkout_unavailable') || 'Checkout is unavailable right now.');
      return;
    }

    if (!form.name || !form.email) {
      setSubmitError(t('checkout_missing_fields') || 'Please fill name and email.');
      return;
    }

    setSubmitting(true);
    try {
      const order = {
        createdAt: new Date().toISOString(),
        status: 'new',
        currency,
        total,
        savings: totalSavings,
        userId: auth.profile.uid,
        partnerSlug: partnerKey || null,
        partnerName: partner?.brandName || partner?.name || partner?.companyName || null,
        customer: { ...form },
        items: lines.map((line) => ({
          productId: line.productId,
          qty: line.qty,
          title: line.title,
          unitPrice: line.price,
          subtotal: line.subtotal,
          campaign: line.campaign || null,
        })),
      };
      const ref = firebase.database.ref('shop_orders').push();
      await ref.set(order);
      clearCart(cartScopeKey);
      setSuccessOrderId(ref.key || 'ok');
    } catch (e) {
      console.error('Failed to place order:', e);
      setSubmitError(t('checkout_failed') || 'Failed to place order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout title={t('checkout') || 'Checkout'} partner={partner} homePath={paths.home} bookingPath={paths.booking}>
      <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 6, md: 8 } }}>
        <Typography sx={{ fontWeight: 900, color: theme.text, fontSize: { xs: 34, md: 44 }, letterSpacing: -1 }}>
          {t('checkout') || 'Checkout'}
        </Typography>
        <Typography sx={{ color: theme.muted, mt: 1, mb: 4 }}>
          {t('checkout_subtitle') || 'Confirm details and place your order.'}
        </Typography>

        {!shopEnabled ? (
          <Paper
            elevation={0}
            sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.border}`, bgcolor: theme.sectionAltBg }}
          >
            <Typography sx={{ fontWeight: 900, color: theme.text }}>
              {t('shop_disabled') || 'Shop is disabled'}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.muted, mt: 0.5 }}>
              {t('shop_disabled_help') || 'Please check back later.'}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/')}
              sx={{ mt: 2, borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
            >
              {t('home') || 'Home'}
            </Button>
          </Paper>
        ) : !lines.length ? (
          <Paper
            elevation={0}
            sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.border}`, bgcolor: theme.sectionAltBg }}
          >
            <Typography sx={{ fontWeight: 900, color: theme.text }}>
              {t('cart_empty') || 'Your cart is empty'}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.muted, mt: 0.5 }}>
              {t('cart_empty_help') || 'Browse the shop and add items to get started.'}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(paths.shop)}
              sx={{ mt: 2, borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
            >
              {t('go_to_shop') || 'Go to shop'}
            </Button>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {successOrderId ? (
              <Alert severity="success">
                {(t('order_placed') || 'Order placed!') + ' ' + (t('order_id') || 'Order ID') + ': ' + successOrderId}
              </Alert>
            ) : null}

            {!isLoggedIn ? (
              <Alert
                severity="info"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => navigate('/login', { state: { from: paths.checkout } })}
                  >
                    {t('login') || 'Log in'}
                  </Button>
                }
              >
                {t('login_to_checkout') || 'Please log in to checkout.'}
              </Alert>
            ) : null}

            {submitError ? <Alert severity="error">{submitError}</Alert> : null}

            <Paper
              elevation={0}
              sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.border}`, bgcolor: theme.cardBg }}
            >
              <Typography sx={{ fontWeight: 900, color: theme.text, mb: 1.5 }}>
                {t('order_summary') || 'Order summary'}
              </Typography>
              <Stack spacing={1}>
                {lines.map((line) => (
                  <Stack key={line.productId} direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ color: theme.muted }}>
                      {line.title} x{line.qty}
                    </Typography>
                    <Typography sx={{ color: theme.text, fontWeight: 800 }}>
                      {formatMoney(line.subtotal, currency)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
              <Divider sx={{ my: 2 }} />
              {totalSavings ? (
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ color: theme.muted }}>{t('savings') || 'Savings'}</Typography>
                  <Typography sx={{ fontWeight: 900, color: theme.text }}>
                    {formatMoney(totalSavings, currency)}
                  </Typography>
                </Stack>
              ) : null}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ color: theme.muted }}>{t('total') || 'Total'}</Typography>
                <Typography sx={{ fontWeight: 900, color: theme.text, fontSize: 18 }}>
                  {formatMoney(total, currency)}
                </Typography>
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.border}`, bgcolor: theme.cardBg }}
            >
              <Typography sx={{ fontWeight: 900, color: theme.text, mb: 1.5 }}>
                {t('customer_details') || 'Customer details'}
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label={t('full_name') || 'Full name'}
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label={t('email') || 'Email'}
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label={t('phone') || 'Phone'}
                  value={form.phone}
                  onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label={t('address') || 'Address'}
                  value={form.address}
                  onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={2}
                />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={() => navigate(paths.cart)}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
                  >
                    {t('back_to_cart') || 'Back to cart'}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={placeOrder}
                    disabled={submitting || !isLoggedIn || Boolean(successOrderId)}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 900,
                      bgcolor: theme.accent || '#111827',
                      '&:hover': { bgcolor: theme.accent || '#0b1224' },
                    }}
                  >
                    {submitting ? (t('placing_order') || 'Placing order...') : (t('place_order') || 'Place order')}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        )}
      </Box>
    </PublicLayout>
  );
}
